import React from 'react';
import { Link } from 'react-router-dom';

export function HelpView() {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Link to="/" style={{ fontSize: '13px', display: 'inline-block', marginBottom: '16px' }}>← Back to dashboard</Link>
      <h1 style={{ fontSize: '22px', fontWeight: 500, marginBottom: '8px' }}>Help</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', fontSize: '14px' }}>
        A quick guide to using AI Readiness and improving your score.
      </p>

      <Section title="What is AI Readiness?">
        <p>
          AI Readiness measures how well your Mercury data is set up for AI-powered tools.
          The better your data, the more effectively our AI can match candidates to vacancies,
          surface the right people, and save you time.
        </p>
        <p>
          Your score is calculated automatically based on your live Mercury data. It updates
          every time you open your dashboard.
        </p>
      </Section>

      <Section title="Your score">
        <p>
          Your overall score is a percentage shown in the ring at the top of your dashboard.
          It's the average of six factors, each scored from 0% to 100%:
        </p>
        <FactorList />
        <p>
          Each factor looks at your active vacancies and in-process candidates in Mercury.
          The higher the percentage, the better your data quality for that area.
        </p>
      </Section>

      <Section title="Your worklist">
        <p>
          Below your score you'll see a worklist — these are the specific items that need
          attention. Each row is a vacancy or candidate that's missing something.
        </p>
        <p>You can filter the worklist by issue type using the tabs at the top.</p>
        <p>For each item, you'll see:</p>
        <ul>
          <li><strong>Name</strong> — the candidate or vacancy</li>
          <li><strong>Client</strong> — the company</li>
          <li><strong>Issue</strong> — what's missing or needs fixing</li>
          <li><strong>Actions</strong> — buttons to resolve the issue</li>
        </ul>
      </Section>

      <Section title="Fixing issues">
        <p>Most issues can be resolved with one click from the worklist. Here's what each action does:</p>

        <H4>Missing CV</H4>
        <p>
          A candidate is in-process on one of your vacancies but has no CV attached in Mercury.
          Click <strong>Upload CV</strong> to attach one directly, or <strong>Open in Mercury</strong> to
          fix it there.
        </p>

        <H4>Missing source or sector tag</H4>
        <p>
          A candidate is missing their source (where they came from) or sector tag.
          Click <strong>Add Source / Sector</strong> to add the missing tags.
          This writes directly to Mercury so it appears on their contact record.
        </p>

        <H4>No ad-response call</H4>
        <p>
          A candidate applied to one of your vacancies but there's no logged call against them.
          Click <strong>Log Call</strong> to record that you've spoken to them.
        </p>

        <H4>Stale vacancy</H4>
        <p>
          A vacancy has been open longer than the threshold (3 months for contract, 6 months for permanent)
          with no recent activity. Click <strong>Close</strong> if it's no longer active, or
          <strong>Still Open</strong> if you're still working it.
        </p>

        <H4>28-day flag</H4>
        <p>
          A vacancy has had no activity in the last 28 days. Same options — close it or confirm it's still active.
          These appear as a separate warning section on your dashboard.
        </p>

        <H4>Missing address, currency, or solution</H4>
        <p>
          A vacancy is missing its country, has a currency that doesn't match the country,
          or has no solution assigned. Click the action button to fix the specific field
          in Mercury.
        </p>

        <H4>Acknowledge</H4>
        <p>
          If you can't resolve an issue right now, you can acknowledge it. This lifts your
          score for the current period but the issue will come back next month if it's still
          unresolved.
        </p>
      </Section>

      <Section title="Team and territory views">
        <p>
          If you're a team lead, you'll see a <strong>Team</strong> tab in the sidebar showing
          your team members' scores, who's doing well, and who needs support.
        </p>
        <p>
          Directors see a <strong>Territory</strong> tab with a breakdown of all teams in their
          territory, including a leaderboard.
        </p>
      </Section>

      <Section title="Tips for a high score">
        <ul>
          <li>Make sure every in-process candidate has a CV attached</li>
          <li>Add source and sector tags to all your candidates</li>
          <li>Log calls against ad-response candidates promptly</li>
          <li>Close or update vacancies you're no longer working on</li>
          <li>Check that vacancy country, currency, and solution fields are filled in</li>
          <li>Review your worklist weekly — a few minutes keeps your score high</li>
        </ul>
      </Section>

      <Section title="Need help?">
        <p>
          If something doesn't look right or you have a question, contact your team lead
          or email <a href="mailto:jason@saragossa.io">jason@saragossa.io</a>.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '32px' }}>
      <h2 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '12px', color: 'var(--accent-teal)' }}>{title}</h2>
      <div style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {children}
      </div>
    </div>
  );
}

function H4({ children }: { children: React.ReactNode }) {
  return <h4 style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', margin: '8px 0 2px' }}>{children}</h4>;
}

function FactorList() {
  const factors = [
    { name: 'CV on in-process', desc: 'Do your in-process candidates have CVs attached?' },
    { name: 'Ad-response coverage', desc: 'Have you called back candidates who applied to your ads?' },
    { name: 'Source & sector', desc: 'Do your candidates have source and sector tags?' },
    { name: 'Candidates added (30d)', desc: 'Are you hitting your monthly target for new candidates? Counts half as much as the others.' },
    { name: 'Stale vacancies', desc: 'Are any of your vacancies past the age threshold with no activity?' },
    { name: 'Address, currency & solution', desc: 'Do your vacancies have the right location and billing fields?' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' }}>
      {factors.map(f => (
        <div key={f.name} style={{ display: 'flex', gap: '12px', alignItems: 'baseline' }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 500, minWidth: 220 }}>{f.name}</span>
          <span>{f.desc}</span>
        </div>
      ))}
    </div>
  );
}
